import { Button } from "@l30/ui";
import { createRoute, Link } from "@tanstack/react-router";
import { z } from "zod";

import { tenantScopeRoute } from "../tenant-scope.js";

/**
 * Portal auth-error landing page.
 *
 * The magic-link verify endpoint redirects here on failure with
 * a coarse `reason` query param (`invalid` | `revoked`). The reason
 * categories are deliberately broad — the API doesn't tell the
 * caller which specific failure occurred to avoid signalling
 * whether a token was once valid.
 *
 * Canon: docs/enhancements/implementation-plan-2026-05.md §9 Day 2.
 */
export const portalAuthErrorRoute = createRoute({
  getParentRoute: () => tenantScopeRoute,
  path: "/portal/auth/error",
  validateSearch: z.object({
    reason: z.enum(["invalid", "revoked"]).optional(),
  }),
  component: PortalAuthError,
});

function PortalAuthError() {
  const { reason } = portalAuthErrorRoute.useSearch();
  const { tenantSlug } = portalAuthErrorRoute.useParams();

  const heading =
    reason === "revoked" ? "Access has been revoked" : "This sign-in link doesn't work";

  const explanation =
    reason === "revoked"
      ? "Your portal access has been removed by your administrator. Contact them if you think this is a mistake."
      : "The link may have expired, already been used, or been mistyped. Request a new one to try again.";

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-muted px-4">
      <div className="w-full max-w-md space-y-6 rounded-lg border bg-surface p-8 text-center shadow-sm">
        <div className="text-lg font-bold tracking-[0.18em] text-(--color-brand)">LATITUDE30</div>
        <div className="space-y-3">
          <h1 className="text-2xl font-semibold text-fg">{heading}</h1>
          <p className="text-sm text-fg-muted">{explanation}</p>
        </div>
        {reason !== "revoked" && (
          <Link to="/t/$tenantSlug/portal/sign-in" params={{ tenantSlug }}>
            <Button className="w-full">Request a new link</Button>
          </Link>
        )}
      </div>
    </div>
  );
}
